/**
 * The chart's symbol, as a control rather than a label.
 *
 * It sits **below** the chart, against the tab bar. The symbol is what is
 * reached for most often and the top of a phone is the one place a thumb cannot
 * go — the desktop toolbar's position is a mouse's convenience, not a person's.
 *
 * One control, two gestures, split on **distance**:
 *
 * - moved less than {@link TAP_SLOP} ⇒ a **tap** → the full symbol search, over
 *   every symbol there is (`SymbolDialog`, reused whole).
 * - moved further ⇒ a **wheel** over the symbols of the list as shown.
 *
 * Time was the other way to tell them apart and is worse: a long-press makes
 * the common case wait on a timer, while the uncommon case is the one that
 * already involves moving. Distance costs nothing and decides itself.
 *
 * The wheel opens on the *first* press and is dismissed on release if nothing
 * moved. Opening it only once the finger has travelled would mean the wheel
 * appears with the finger already a row down it, and the first row you can
 * choose is not the one you started on.
 */

import { useRef, useState } from "react";

import { SymbolWheel, wheelIndex, type WheelSymbol } from "./SymbolWheel";

/** Under this many pixels of travel, it was a tap. */
export const TAP_SLOP = 22;

export interface SymbolGripProps {
  /** The chart's symbol. */
  symbol: string;
  /** Last price and change, already formatted — the grip does no arithmetic. */
  price?: { text: string; direction: "up" | "down" | "flat" };
  /** The list as shown, for the wheel. Empty ⇒ the drag does nothing. */
  symbols: readonly WheelSymbol[];
  /** A symbol was chosen from the wheel, on release. */
  onPick: (symbol: string) => void;
  /** The grip was tapped. Opens the search. */
  onSearch: () => void;
  /** Renders the wheel into the chart stage, which is not this component's box. */
  renderWheel: (wheel: React.ReactNode) => void;
}

/**
 * The symbol's size on the phone's bottom row: 15px down to a 12px floor as it
 * grows past five letters (owner, 2026-10-13; `docs/mockups/form-factors.html`).
 */
export function symbolFontPx(symbol: string): number {
  return Math.max(12, 15 - Math.max(0, symbol.length - 5));
}

const GRIP: React.CSSProperties = {
  // Shrinks into what the row leaves, so the keys either side never move.
  flex: "0 1 auto",
  minWidth: 0,
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  padding: "4px 8px",
  margin: "-4px -8px",
  borderRadius: 7,
  border: 0,
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  // The gesture is ours: without this the browser claims the vertical drag for
  // a scroll and the wheel never sees a move at all.
  touchAction: "none",
  WebkitTapHighlightColor: "transparent",
};

export function SymbolGrip({
  symbol,
  price,
  symbols,
  onPick,
  onSearch,
  renderWheel,
}: SymbolGripProps) {
  const [held, setHeld] = useState(false);
  const drag = useRef<{ id: number; x: number; y: number; from: number; index: number } | null>(null);

  const start = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (drag.current) return;
    const from = Math.max(
      0,
      symbols.findIndex((s) => s.symbol.toUpperCase() === symbol.toUpperCase()),
    );
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, from, index: from };
    event.currentTarget.setPointerCapture(event.pointerId);
    setHeld(true);
    if (symbols.length > 0) renderWheel(<SymbolWheel symbols={symbols} index={from} />);
  };

  const move = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId || symbols.length === 0) return;
    // Up is forward: `y - clientY` grows as the finger rises.
    const next = wheelIndex(state.from, state.y - event.clientY, symbols.length);
    if (next === state.index) return;
    state.index = next;
    renderWheel(<SymbolWheel symbols={symbols} index={next} />);
  };

  const end = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    drag.current = null;
    setHeld(false);
    renderWheel(null);

    const travelled = Math.abs(state.y - event.clientY);
    // Mostly sideways is the row's swipe between charts (`MobileApp`), not a
    // tap and not a turn of the wheel: choose nothing.
    const sideways = Math.abs(state.x - event.clientX);
    if (sideways >= TAP_SLOP && sideways > travelled) return;
    if (travelled < TAP_SLOP) {
      onSearch();
      return;
    }
    const picked = symbols[state.index];
    // Not a no-op guard for tidiness: the pick is a refetch, so re-choosing the
    // symbol already on the chart would tear the surface down to rebuild it
    // identically.
    if (picked && picked.symbol.toUpperCase() !== symbol.toUpperCase()) onPick(picked.symbol);
  };

  const cancel = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    // A cancelled pointer — a system gesture taking over, or a call arriving —
    // chooses nothing. Committing whatever the wheel happened to be on would
    // load a symbol the user never released on.
    drag.current = null;
    setHeld(false);
    renderWheel(null);
  };

  return (
    <button
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={cancel}
      aria-label={`Symbol ${symbol} — tap to search, drag up for the list`}
      style={{
        ...GRIP,
        background: held ? "var(--qf-selection)" : "transparent",
        color: "var(--qf-text)",
      }}
    >
      <span
        style={{
          fontSize: symbolFontPx(symbol),
          fontWeight: 600,
          letterSpacing: "0.02em",
          // Past the 12px floor a long symbol ends in "…"; the legend at the
          // top of the chart carries it whole.
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {symbol}
      </span>
      {price && (
        <span
          style={{
            fontSize: 13,
            fontVariantNumeric: "tabular-nums",
            color:
              price.direction === "up"
                ? "var(--qf-positive)"
                : price.direction === "down"
                  ? "var(--qf-negative)"
                  : "var(--qf-text-secondary)",
          }}
        >
          {price.text}
        </span>
      )}
    </button>
  );
}
